import { useRoute } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import { Platform } from 'react-native';
import { SvgUri } from 'react-native-svg';

import { PaymentConfig } from '~/api-services/PaymentConverterService/types';
import config from '~/config';
import { isDesktop } from '~/constants/scale';
import { useAddFunds } from '~/hooks/useAddFunds';
import { linkTo } from '~/navigation';
import paths from '~/navigation/paths';
import ResolveCountryService from '~/api-services/ResolveCountryService';
import { isEUCountry } from '~/lib/utils';
import FlowScreen from '../AddFundsShared/FlowScreen';
import OptionRow from '../AddFundsShared/OptionRow';

type Props = {
    setActiveRightTab?: Function;
    currentAmount?: string;
    setProvider?: Function;
    setPaytikoPaymentProvider?: Function;
};

export default function AddFundsCategory({
    setActiveRightTab,
    currentAmount,
    setProvider,
    setPaytikoPaymentProvider,
}: Props) {
    const route = useRoute();
    const amount = currentAmount ? currentAmount : (route.params as any).amount;
    const { configData } = useAddFunds(amount != '' ? Number(amount) : 0);
    const [enableSkrillLogo, setEnableSkrillLogo] = useState(false);

    useEffect(() => {
        (async () => {
            const { status, data } = await ResolveCountryService.resolveCountry();
            if (status === 200 && data.status) {
                let clientCountry = data.returnData;
                if (!isEUCountry(clientCountry) && clientCountry !== 'GB') {
                    setEnableSkrillLogo(true);
                }
            }
        })();
    }, []);

    function openPaymentCategory(cProvider: string, cProviderId: number) {
        if (setProvider) {
            setProvider(cProvider);
        }
        if (setPaytikoPaymentProvider) {
            setPaytikoPaymentProvider(cProviderId);
        }
        if (setActiveRightTab) {
            setActiveRightTab('add-money-card');
        }
    }

    function openPaymentCategoryPaytiko(cProvider: string) {
        if (setProvider) {
            setProvider(cProvider);
        }
        if (setActiveRightTab) {
            setActiveRightTab('add-money-card-paytiko');
        }
    }

    function openPaymentCategoryPaymentProvider1Select(cProvider: string) {
        if (setProvider) {
            setProvider(cProvider);
        }
        if (setActiveRightTab) {
            setActiveRightTab('add-money-card-payment-provider1-select');
        }
    }

    function RenderCards() {
        if (!configData.card) {
            return null;
        }
        if (configData.card.length == 0) {
            return null;
        }
        return configData.card.map((paymentConfig: PaymentConfig, key) => (
            <RenderCard paymentConfig={paymentConfig} key={key} />
        ));
    }

    function RenderCard({ paymentConfig }) {
        if (paymentConfig.provider === 'PAYMENT_PROVIDER_1') {
            return <RenderCardPaymentProvider1Select paymentConfig={paymentConfig} />;
        }
        if (paymentConfig.provider === 'PAYTIKODIRECTCARD' || paymentConfig.provider === 'ARENAMONEYDIRECTCARD') {
            if (!paymentConfig.paytikoPaymentProviders) {
                return null;
            }
            if (paymentConfig.paytikoPaymentProviders.length === 0) {
                return null;
            }
            if (paymentConfig.paytikoPaymentProviders.length === 1) {
                const paytikoProviderId = paymentConfig.paytikoPaymentProviders[0].id;
                return <RenderCardNormal paymentConfig={paymentConfig} paytikoProviderId={paytikoProviderId} />;
            }
            return <RenderCardPaytiko paymentConfig={paymentConfig} />;
        }
        return <RenderCardNormal paymentConfig={paymentConfig} paytikoProviderId={-1} />;
    }

    function makeWebsiteKey(): string {
        return config.WEBSITE === 'FXMERIDIAN' || config.WEBSITE === 'INVESTIGNFF' ? 'FXMERIDIAN-' : '';
    }

    function RenderCardPaytiko({ paymentConfig }) {
        const onPress = () => {
            isDesktop() && setActiveRightTab
                ? openPaymentCategoryPaytiko(paymentConfig.provider)
                : linkTo(
                      paths.addFundsCardPaytiko.replace(':provider', paymentConfig.provider).replace(':amount', amount)
                  );
        };
        return <RenderCardCommon paymentConfig={paymentConfig} onPress={onPress} />;
    }

    function RenderCardPaymentProvider1Select({ paymentConfig }) {
        const onPress = () => {
            isDesktop() && setActiveRightTab
                ? openPaymentCategoryPaymentProvider1Select(paymentConfig.provider)
                : linkTo(
                      paths.addFundsCardPaymentProvider1Select
                          .replace(':provider', paymentConfig.provider)
                          .replace(':amount', amount)
                  );
        };
        return <RenderCardCommon paymentConfig={paymentConfig} onPress={onPress} />;
    }

    function RenderCardNormal({ paymentConfig, paytikoProviderId }) {
        const onPress = () => {
            isDesktop() && setActiveRightTab
                ? openPaymentCategory(paymentConfig.provider, paytikoProviderId)
                : linkTo(
                      paths.addFundsCard
                          .replace(':provider', paymentConfig.provider)
                          .replace(':paytikoProvider', paytikoProviderId.toString())
                          .replace(':amount', amount)
                  );
        };
        return <RenderCardCommon paymentConfig={paymentConfig} onPress={onPress} />;
    }

    function RenderCardCommon({ paymentConfig, onPress }) {
        const websiteKey = makeWebsiteKey();
        const descriptionKey = websiteKey + 'credit-and-debit-cards-' + paymentConfig.provider;
        let imageSource;
        if (paymentConfig.provider === 'PAYTIKO') {
            imageSource = enableSkrillLogo
                ? require('../../../../assets/files/mobileplatform/Paytiko/crypto_skrill_visa.gif')
                : require('../../../../assets/files/mobileplatform/Paytiko/visa_master_crypto.gif');
        } else {
            imageSource = require('../../../../assets/files/mobileplatform/images/payments/visa_mastercard.gif');
        }
        return <OptionRow label={descriptionKey} onPress={onPress} image={imageSource} />;
    }

    function RenderAlternative() {
        if (!configData.alternative) {
            return null;
        }
        if (configData.alternative.length == 0) {
            return null;
        }
        return (
            <OptionRow
                label={'alternative-payment-method'}
                onPress={() => {
                    isDesktop() && setActiveRightTab
                        ? setActiveRightTab('add-money-alternative')
                        : linkTo(paths.addFundsAlternative.replace(':amount', amount));
                }}
                image={
                    Platform.OS === 'web'
                        ? require('../../../../assets/files/mobileplatform/images/payments/Only_alternativepayment.svg')
                        : undefined
                }
                imageNode={
                    Platform.OS === 'web' ? undefined : (
                        <SvgUri
                            width="100%"
                            height="100%"
                            uri={
                                'https://' +
                                config.EXTERNAL_SOURCES_URL +
                                'mobileplatform/images/payments/Only_alternativepayment.svg'
                            }
                        />
                    )
                }
            />
        );
    }

    function RenderBank() {
        return (
            <OptionRow
                label={'secure-wire-transfer'}
                onPress={() => {
                    isDesktop() && setActiveRightTab
                        ? setActiveRightTab('add-money-bank')
                        : linkTo(paths.addFundsBank.replace(':amount', amount));
                }}
                image={
                    Platform.OS === 'web'
                        ? require('../../../../assets/files/mobileplatform/images/payments/Only_WireTransfer.svg')
                        : undefined
                }
                imageNode={
                    Platform.OS === 'web' ? undefined : (
                        <SvgUri
                            width="100%"
                            height="100%"
                            uri={
                                'https://' +
                                config.EXTERNAL_SOURCES_URL +
                                'mobileplatform/images/payments/Only_WireTransfer.svg'
                            }
                        />
                    )
                }
            />
        );
    }

    return (
        <FlowScreen
            title="select-prefered-payment-method"
            goToScreenName="add-money"
            setActiveRightTab={setActiveRightTab}
        >
            {configData ? (
                <>
                    <RenderCards />
                    <RenderAlternative />
                    <RenderBank />
                </>
            ) : null}
        </FlowScreen>
    );
}
